/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

// src/components/DataTable/DataTable.js

import React, { useState, useCallback } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Flex,
  Progress,
  Box,
  useColorModeValue,
  VStack,
  Icon,
} from "@chakra-ui/react";
import { FiInbox } from "react-icons/fi";
import SplashScreen from "../SplashScreen/SplashScreen";
import ResponsiveTableWrapper from "../Layout/ResponsiveTableWrapper";

function DataTable({
  title,
  filterBar,
  columns,
  data = [],
  sortColumn = null,
  sortOrder = "asc",
  onSort = () => {},
  tableFontSize = "12px",
  tableRowHeight = "36px",
  onRowClick = null,
  onRowDoubleClick = null,
  onRowContextMenu = null,
  emptyMessage = "Henüz liste yüklenmedi. Filtreleri doldurup 'Listele' butonuna basarak arama yapabilirsiniz.",
  loading = false,
  hasSearched = false,
  columnOrder = null,
  onColumnOrderChange = null,
  minTableWidth = "900px",
  flat = false,
  topPadding = { base: "120px", md: "45px" },
}) {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const displayColumns = React.useMemo(() => {
    if (columnOrder && columnOrder.length > 0) {
      const ordered = columnOrder
        .map((acc) => columns.find((c) => c.accessor === acc))
        .filter(Boolean);
      const missing = columns.filter((c) => !columnOrder.includes(c.accessor));
      return ordered.length > 0 ? [...ordered, ...missing] : columns;
    }
    return columns;
  }, [columns, columnOrder]);

  const handleDragStart = useCallback(
    (e, index) => {
      if (!onColumnOrderChange) return;
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
      e.dataTransfer.setData("application/json", JSON.stringify({ index }));
    },
    [onColumnOrderChange]
  );

  const handleDragOver = useCallback(
    (e, index) => {
      if (!onColumnOrderChange || draggedIndex === null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverIndex(index);
    },
    [onColumnOrderChange, draggedIndex]
  );

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e, dropIndex) => {
      if (!onColumnOrderChange || draggedIndex === null) return;
      e.preventDefault();
      setDraggedIndex(null);
      setDragOverIndex(null);
      const accessors = displayColumns.map((c) => c.accessor);
      const [removed] = accessors.splice(draggedIndex, 1);
      accessors.splice(dropIndex, 0, removed);
      onColumnOrderChange(accessors);
    },
    [onColumnOrderChange, draggedIndex, displayColumns]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("white", "gray.700");
  const cardBg = useColorModeValue("white", "navy.800");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const thBg = useColorModeValue("gray.50", "gray.700");

  return (
    <>
    <Flex direction="column" pt={topPadding} gap={4}>
      <Box
        bg={flat ? "transparent" : cardBg}
        borderRadius={flat ? "0" : "15px"}
        boxShadow={flat ? "none" : "md"}
        overflow="hidden"
        border={flat ? "none" : "1px solid"}
        borderColor={flat ? "transparent" : borderColor}
        opacity={loading ? 0.5 : 1}
        transition="opacity 0.3s"
        position="relative"
      >
        {/* Splash Screen - Sadece tablo alanında göster */}
        <SplashScreen 
          isLoading={loading} 
          message="Veriler yükleniyor..."
          fullScreen={false}
        />
        {/* Header kısmı */}
        <Flex
          justify="space-between"
          align="center"
          px={4}
          py={2.5}
          borderBottom="1px solid"
          borderColor={borderColor}
          position="sticky"
          top={0}
          zIndex={10}
          bg={headerBg}
        >
          <Text fontSize="14px" fontWeight="bold" color={textColor}>
            {title}
          </Text>
          {filterBar}
        </Flex>

        {/* Tablo */}
        <ResponsiveTableWrapper maxH="600px" minTableWidth={minTableWidth}>
          {!loading && data.length === 0 ? (
            <VStack py={12} spacing={3} color="gray.400">
              <Icon as={FiInbox} boxSize={10} />
              <Text fontSize="sm" color="gray.500" textAlign="center" px={4}>
                {hasSearched 
                  ? "Arama kriterlerinize uygun kayıt bulunamadı."
                  : emptyMessage}
              </Text>
            </VStack>
          ) : (
            <Box pr={2}>
              <Table
                variant="simple"
                color={textColor}
                w="100%"
                sx={{
                  "th": {
                    py: 1.5,
                    px: 2.5,
                    fontSize: "12px",
                    fontWeight: "600",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    verticalAlign: "middle",
                    bg: `${thBg} !important`,
                    color: textColor,
                    borderBottom: "2px solid",
                    borderColor: borderColor,
                  },
                  "td": {
                    py: 1.5,
                    px: 2.5,
                    fontSize: tableFontSize,
                    fontWeight: "normal",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    verticalAlign: "middle",
                  },
                  tr: { 
                    height: tableRowHeight,
                    transition: "background-color 0.2s",
                  },
                }}
              >
              <Thead position="sticky" top={0} zIndex={10} bg={headerBg}>
                <Tr>
                  {displayColumns.map((col, index) => (
                    <Th
                      key={col.accessor}
                      cursor={col.sortable ? "pointer" : onColumnOrderChange ? "grab" : "default"}
                      onClick={() => col.sortable && onSort(col.accessor)}
                      bg={headerBg}
                      draggable={!!onColumnOrderChange}
                      onDragStart={(e) => onColumnOrderChange && handleDragStart(e, index)}
                      onDragOver={(e) => onColumnOrderChange && handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => onColumnOrderChange && handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      opacity={draggedIndex === index ? 0.5 : 1}
                      borderLeft={dragOverIndex === index ? "3px solid" : undefined}
                      borderColor={dragOverIndex === index ? "blue.400" : undefined}
                      userSelect="none"
                      _active={onColumnOrderChange ? { cursor: "grabbing" } : undefined}
                    >
                      {col.Header}
                      {col.sortable &&
                        (sortColumn !== col.accessor ? " ↕" : sortOrder === "asc" ? " ▲" : " ▼")}
                    </Th>
                  ))}
                </Tr>
              </Thead>

              <Tbody>
                {data.map((row, index) => (
                <Tr
                  key={row.id ?? `row-${index}`}
                  borderBottom="1px solid"
                  borderColor={borderColor}
                  onClick={
                    onRowClick ? () => onRowClick(row) : undefined
                  }
                  onDoubleClick={
                    onRowDoubleClick ? () => onRowDoubleClick(row) : undefined
                  }
                  onContextMenu={(e) =>
                    onRowContextMenu ? onRowContextMenu(e, row) : undefined
                  }
                  _hover={{
                    bg: hoverBg,
                    cursor:
                      onRowDoubleClick || onRowContextMenu
                        ? "pointer"
                        : "default",
                    transform: "scale(1.001)",
                    transition: "all 0.2s",
                  }}
                >
                  {displayColumns.map((col) => {
                    const value = row[col.accessor];

                    // Eğer Cell render fonksiyonu varsa onu kullan
                    if (col.Cell && typeof col.Cell === "function") {
                      return (
                        <Td key={col.accessor} isNumeric={col.isNumeric}>
                          {col.Cell({ value, row })}
                        </Td>
                      );
                    }

                    // Blokaj Rölesi özel ikon
                    if (col.accessor === "blokajRoles") {
                      return (
                        <Td key={col.accessor}>
                          {value ? "🔒 Aktif" : "🔓 Pasif"}
                        </Td>
                      );
                    }

                    // Kalan gün özel tasarım
                    if (col.accessor === "kalanGun") {
                      let renk = "green.400";
                      if (value <= 2) renk = "red.600";
                      else if (value <= 7) renk = "orange.500";
                      else if (value <= 15) renk = "yellow.500";
                      else if (value <= 30) renk = "green.300";
                      const pct = Math.max(
                        0,
                        (value / (row.abonelikTipi * 30)) * 100
                      );
                      return (
                        <Td key={col.accessor} isNumeric p={0} m={0}>
                          <Text
                            fontWeight="bold"
                            color={renk}
                            fontSize={tableFontSize}
                          >
                            {value} gün
                          </Text>
                          <Progress
                            value={pct}
                            size="xs"
                            colorScheme={renk.split(".")[0]}
                            height="3px"
                            borderRadius="md"
                            mt="1px"
                          />
                        </Td>
                      );
                    }

                    let displayValue = value;
                    if (
                      displayValue === undefined ||
                      displayValue === null ||
                      (typeof displayValue === "string" &&
                        displayValue.trim() === "")
                    ) {
                      displayValue = "-";
                    }

                    return (
                      <Td key={col.accessor} isNumeric={col.isNumeric}>
                        {displayValue}
                      </Td>
                    );
                  })}
                </Tr>
              ))}
            </Tbody>
          </Table>
            </Box>
          )}
        </ResponsiveTableWrapper>
      </Box>
    </Flex>
    </>
  );
}

export default React.memo(DataTable);
